/*
  Wishlist page, driven by Swym's SDK in SDK-Only Mode.

  Swym holds the data; the theme draws the page. For each saved item the SDK
  gives us a product URL, so we ask Shopify's Section Rendering API for a real
  theme-rendered card against that URL rather than rebuilding one from the
  SDK's title/image/price. That is the whole point: a wishlist card is then
  identical to a collection card — swatches, fit badges, sale badge, gating —
  and inherits every future card fix.

  Swym API (SDK-Only Mode):
    swat.fetchLists({ callbackFn, errorFn })   -> lists[0].lid, .listcontents[]
    swat.deleteFromList(lid, { epi, empi, du }, success, error)
  Item fields: epi variant id, empi product id, du product url, dt title,
  iu image, pr price, stk stock.
*/
(function () {
  'use strict';

  var SECTION = 'wishlist-card';

  function init(root) {
    if (root.wishlistReady) return;
    root.wishlistReady = true;

    var grid = root.querySelector('[data-wishlist-grid]');
    var loading = root.querySelector('[data-wishlist-loading]');
    var empty = root.querySelector('[data-wishlist-empty]');
    var notice = root.querySelector('[data-wishlist-notice]');
    var errorBox = root.querySelector('[data-wishlist-error]');
    var count = root.querySelector('[data-wishlist-count]');

    root.style.setProperty('--wishlist-cols-d', root.dataset.columnsDesktop || 4);
    root.style.setProperty('--wishlist-cols-m', root.dataset.columnsMobile || 2);

    var listId = null;

    function show(el) {
      el && el.removeAttribute('hidden');
    }
    function hide(el) {
      el && el.setAttribute('hidden', '');
    }

    function showError() {
      hide(loading);
      hide(grid);
      hide(empty);
      show(errorBox);
    }

    function showEmpty() {
      hide(loading);
      hide(grid);
      show(empty);
      hide(notice);
    }

    /* One card, rendered by the theme against the product's own URL. */
    function fetchCard(item) {
      var url = item.du;
      if (!url) return Promise.resolve(null);

      // du can be absolute; the Section Rendering API wants a same-origin path.
      var path;
      try {
        path = new URL(url, window.location.origin).pathname;
      } catch (e) {
        return Promise.resolve(null);
      }

      return fetch(path + '?section_id=' + SECTION, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('card ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var holder = document.createElement('div');
          holder.innerHTML = html.trim();
          var card = holder.querySelector('[data-wishlist-card]');
          if (!card) return null;
          card.dataset.epi = item.epi;
          card.dataset.empi = item.empi;
          card.dataset.du = item.du;
          return card;
        })
        .catch(function () {
          // A deleted or unpublished product should drop out quietly rather
          // than taking the whole page down with it.
          return null;
        });
    }

    function render(items) {
      if (!items || !items.length) {
        showEmpty();
        return;
      }

      Promise.all(items.map(fetchCard)).then(function (cards) {
        var live = cards.filter(Boolean);

        if (!live.length) {
          showEmpty();
          return;
        }

        var fragment = document.createDocumentFragment();
        live.forEach(function (card) {
          fragment.appendChild(card);
        });

        grid.innerHTML = '';
        grid.appendChild(fragment);

        hide(loading);
        hide(empty);
        show(grid);
        show(notice);

        if (count) {
          count.textContent = live.length + (live.length === 1 ? ' item' : ' items');
          show(count);
        }
      });
    }

    function remove(card) {
      if (!listId) return;

      card.classList.add('is-removing');

      window.SwymCallbacks.push(function (swat) {
        swat.deleteFromList(
          listId,
          {
            epi: parseInt(card.dataset.epi, 10),
            empi: parseInt(card.dataset.empi, 10),
            du: card.dataset.du
          },
          function () {
            card.addEventListener('transitionend', function handler() {
              card.removeEventListener('transitionend', handler);
              card.remove();
              if (!grid.querySelector('[data-wishlist-card]')) showEmpty();
              else if (count) {
                var n = grid.querySelectorAll('[data-wishlist-card]').length;
                count.textContent = n + (n === 1 ? ' item' : ' items');
              }
            });
            // If the transition does not fire, still remove it.
            setTimeout(function () {
              if (card.isConnected) card.remove();
            }, 400);
          },
          function () {
            // Put it back rather than leaving a card that looks removed but is not.
            card.classList.remove('is-removing');
          }
        );
      });
    }

    grid.addEventListener('click', function (event) {
      var button = event.target.closest('[data-wishlist-remove]');
      if (!button) return;
      event.preventDefault();
      var card = button.closest('[data-wishlist-card]');
      card && remove(card);
    });

    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(function (swat) {
      swat.fetchLists({
        callbackFn: function (lists) {
          if (!lists || !lists.length) {
            showEmpty();
            return;
          }
          listId = lists[0].lid;
          render(lists[0].listcontents);
        },
        errorFn: showError
      });
    });

    // Signing in merges a guest list into the account's, so redraw when Swym
    // says the customer changed.
    document.addEventListener('sw:customerInfoRefreshed', function () {
      root.wishlistReady = false;
      init(root);
    });
  }

  function scan() {
    document.querySelectorAll('[data-wishlist]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
})();
